import type { CommandContext, CommandDefinition } from "./types";

/**
 * Canonical list of application commands. Every menu bar item, context menu
 * entry, keyboard shortcut, and command palette row should be derivable from
 * this registry.
 *
 * A command here declares metadata only. Handlers are registered at runtime
 * through `CommandProvider` so they can close over component state.
 *
 * Slack-parity shortcuts are the default for navigation/messaging; Mac
 * conventions are unconditional for app/window commands. Ano-specific
 * shortcuts only where Slack has no precedent.
 */
export const commands: readonly CommandDefinition[] = [
  // ─── App ──────────────────────────────────────────────────────────────
  {
    id: "app.openCommandPalette",
    label: "Jump to…",
    section: "go",
    accelerator: "CmdOrCtrl+K",
    scope: "global",
  },
  {
    id: "app.openSettings",
    label: "Settings…",
    section: "app",
    accelerator: "CmdOrCtrl+,",
    scope: "global",
  },
  {
    id: "app.checkForUpdates",
    label: "Check for Updates…",
    section: "app",
  },

  // ─── File / Create ────────────────────────────────────────────────────
  {
    id: "sidebar.toggleNewMenu",
    label: "New…",
    section: "file",
    accelerator: "CmdOrCtrl+N",
    scope: "outside-input",
  },
  {
    id: "sidebar.newDirectMessage",
    label: "New Direct Message",
    section: "file",
    accelerator: "CmdOrCtrl+Shift+K",
    scope: "global",
  },
  {
    id: "sidebar.browseChannels",
    label: "Browse Channels",
    section: "file",
    accelerator: "CmdOrCtrl+Shift+L",
    scope: "global",
  },
  {
    id: "sidebar.newChannel",
    label: "New Channel…",
    section: "file",
    scope: "global",
  },
  {
    id: "sidebar.newWorkspace",
    label: "New Workspace…",
    section: "file",
    scope: "global",
  },

  // ─── Channel navigation ──────────────────────────────────────────────
  {
    id: "channel.switchPrev",
    label: "Previous Channel",
    section: "go",
    accelerator: "Alt+Up",
    scope: "global",
  },
  {
    id: "channel.switchNext",
    label: "Next Channel",
    section: "go",
    accelerator: "Alt+Down",
    scope: "global",
  },

  // ─── View ─────────────────────────────────────────────────────────────
  {
    id: "view.cycleMode",
    label: "Cycle View Mode",
    section: "view",
    // Slack reserves ⌘J for Jump to Most Recent Unread. Once that command is
    // wired, this moves to Ctrl+Cmd+V per plan.
    accelerator: "CmdOrCtrl+J",
    scope: "global",
  },
  {
    id: "view.toggleTerminal",
    label: "Toggle Terminal",
    section: "view",
    accelerator: "Alt+CmdOrCtrl+T",
    scope: "global",
  },

  // ─── Help ─────────────────────────────────────────────────────────────
  {
    id: "help.openKeyboardShortcuts",
    label: "Keyboard Shortcuts",
    section: "help",
    accelerator: "CmdOrCtrl+/",
    scope: "global",
  },
  {
    id: "help.openDocs",
    label: "Ano Docs…",
    section: "help",
  },
  {
    id: "help.reportIssue",
    label: "Report an Issue…",
    section: "help",
  },
  {
    id: "help.sendFeedback",
    label: "Send Feedback…",
    section: "help",
  },

  // ─── Message (active when a message row is focused) ───────────────────
  {
    id: "message.reply",
    label: "Reply in Thread",
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.react",
    label: "Add Reaction…",
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.copyText",
    label: "Copy Text",
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.copyLink",
    label: "Copy Link to Message",
    section: "message",
    accelerator: "CmdOrCtrl+Shift+C",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.pinToggle",
    label: (ctx) => (ctx.isPinned ? "Unpin Message" : "Pin Message"),
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.sendToShell",
    label: "Send to Shell",
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId),
  },
  {
    id: "message.edit",
    label: "Edit Message",
    section: "message",
    when: (ctx) => Boolean(ctx.selectedMessageId) && ctx.isOwnMessage === true,
  },
  {
    id: "message.delete",
    label: "Delete Message…",
    section: "message",
    destructive: true,
    when: (ctx) => Boolean(ctx.selectedMessageId) && ctx.isOwnMessage === true,
  },
] as const;

const commandById = new Map(commands.map((c) => [c.id, c] as const));

export function getCommand(id: string): CommandDefinition | undefined {
  return commandById.get(id);
}

export function listCommands(): readonly CommandDefinition[] {
  return commands;
}

export function listAvailableCommands(
  ctx: CommandContext,
): CommandDefinition[] {
  return commands.filter((c) => (c.when ? c.when(ctx) : true));
}

export function listCommandsBySection(
  section: CommandDefinition["section"],
): CommandDefinition[] {
  return commands.filter((c) => c.section === section);
}
